import { ccEntrySyncManualCcState } from '../components/credit-card-entry.js';
import {
    getPledgePayMemberContext,
    setPledgePayMemberContext,
} from './pledge-pay-member-context';
import { getPledgePayConfig } from './pledge-pay-config';
import { getManagerOverlayElement, showOverlay, hideOverlay } from '../shared/overlay.js';
import { isPledgePayCreditCovered, mgrPledgeBillRunAfterFullyCoveredDelay, mgrPledgeBillShellDismissIfModal, pledgePayUiRoot, runAfterPledgePaymentModalHidden } from './pledge-pay-utils';
import { buildQrPledgePaymentPayload } from './pledge-pay-payload';
import { validatePledgeBillPaymentState } from './pledge-pay-validate';
import { registerDafCardTypeHandler } from '../manager/pledges/daf-card-type-handlers.js';
import { reloadPledgesPage } from '../manager/pledges/runtime-registry';
import { readOpenPayRowIds } from './pledge-pay-row-ids';
import { openQrPayModal } from '../members/qr-pay-modal.js';
import { showAlertMsg } from '../manager/show-alert-msg.js';

import * as FieldValidation from '../shared/field-validation.js';
import { renderSavedCards, refreshSavedCardScroll, clearSavedCardSelection } from '../shared/saved-card-picker.js';
import { mgrPledgeModalHasAnyGatewayCreditApplied } from './pledge-pay-open-payment-gateway.js';
import { isOnlinePaymentKind, isQrPaymentKind, selectedPaymentKind } from '../shared/payment-kind.js';
import { syncCoverSurchargeWithPaymentKind } from './pledge-pay-surcharge.js';
import { isPendingRecurringPayBody } from './pledge-pay-pending-recurring';

export const MGR_PLEDGE_BILL_SAVED_CARDS = '[data-saved-card-picker="mgr_pledge_bill_saved_payment_id"]';

let mgrPledgeBillDafCardType = 'credit';

function getMgrPledgeBillDafCardType() {
    return mgrPledgeBillDafCardType;
}

function setMgrPledgeBillDafCardType(cardType) {
    const next = String(cardType || 'credit');
    if (mgrPledgeBillDafCardType && mgrPledgeBillDafCardType !== next) {
        ['mgr_pledge_bill_card', 'mgr_pledge_bill_cvv2', 'mgr_pledge_bill_expiry_month', 'mgr_pledge_bill_expiry_year', 'mgr_pledge_bill_avs_zip'].forEach(function (id) {
            const el = document.getElementById(id);
            if (el) {
                el.value = '';
            }
        });
    }
    mgrPledgeBillDafCardType = next;
}

function resetMgrPledgeBillDafCardType() {
    mgrPledgeBillDafCardType = 'credit';
}

function mgrPledgeModalSyncPaymentMethodPills($modal) {
    $modal.find('[data-mgr-pledge-method-pill]').each(function () {
        const $pill = $(this);
        const val = String($pill.data('mgrPledgeMethodPill') || '');
        const $radio = $modal.find(`input[name="mgr_pledge_payment_method_pref"][value="${val}"]`);
        const isActive = $radio.prop('checked');
        const isDisabled = $pill.hasClass('is-disabled') || $radio.prop('disabled');
        $pill.toggleClass('is-active', !!isActive);
        $pill.attr('aria-selected', isActive ? 'true' : 'false');
        $pill.prop('disabled', isDisabled);
    });
}

function mgrPledgeModalSyncPaymentMethodPanels($modal) {
    const val = selectedPaymentKind($modal);
    const $qrWrap = $modal.find('#mgr_pledge_qr_pay_wrap');
    const $wrap = $modal.find('#mgr_pledge_bill_card_fields_wrap');
    const savedId = String($modal.find('#mgr_pledge_bill_saved_payment_id').val() || '').trim();
    const showCard = !isPledgePayCreditCovered($modal) && isOnlinePaymentKind(val);

    if ($wrap.length) {
        $wrap.toggle(showCard);
        const showManual = showCard && !savedId;
        $wrap.find('[data-manual-fields-collapse]').toggleClass('is-collapsed', !showManual);
        if (showManual) {
            mgrPledgeBillInitExpiryInputMask($modal);
        }
    }
    if ($qrWrap.length) {
        $qrWrap.toggle(isQrPaymentKind(val));
    }

    const $cashFields = $modal.find('.mark_paid_div');
    if ($cashFields.length) {
        if (val === 'check_cash') {
            $cashFields.removeClass('pledge-pay-hidden').show();
        } else {
            $cashFields.hide().addClass('pledge-pay-hidden');
        }
    }

    const payType = String($modal.find('#payment_type').val() || '');
    const manualNew = payType === 'pay' && showCard && !savedId;
    const daf = getMgrPledgeBillDafCardType();
    const requireZipCvv = daf === 'credit';
    ccEntrySyncManualCcState({
        idPrefix: 'mgr_pledge_bill',
        manualEnabled: manualNew,
        requireBaseManualFields: manualNew,
        requireZipAndCvv: manualNew && requireZipCvv,
        cardType: daf,
    });
    mgrPledgeModalSyncPaymentMethodPills($modal);
    syncCoverSurchargeWithPaymentKind($modal);
    if (showCard) {
        refreshSavedCardScroll($modal);
    }
}

function mgrPledgeModalSyncQrPayButtonText($modal) {
    const $btn = $modal.find('#mgr_pledge_qr_pay_btn');
    if (!$btn.length) {
        return;
    }
    const hasCredit = mgrPledgeModalHasAnyGatewayCreditApplied($modal);
    if ($btn.closest('[data-pledge-express-pay]').length) {
        $btn.attr(
            'aria-label',
            hasCredit ? 'Save applied credit and pay the remainder with QR Pay' : 'Pay with QR Pay'
        );
        return;
    }
    $btn.text(hasCredit
        ? 'save credit application and pay remainder with QR code'
        : 'use QR code to pay'
    );
}

function applyPledgePaySavedCardsToUi($modal, savedCards) {
    const cards = Array.isArray(savedCards) ? savedCards : [];
    const { userId } = getPledgePayMemberContext();
    if (!userId) {
        if (cards.length > 0) {
            throw new Error('applyPledgePaySavedCardsToUi: member context userId is required');
        }
    } else {
        setPledgePayMemberContext(userId, { savedCards: cards });
    }

    const $picker = $modal.find(MGR_PLEDGE_BILL_SAVED_CARDS);
    if (!$picker.length) {
        return;
    }

    const plan = $modal.data('deletedSchedulePlan');
    const savedCardId = plan && Number(plan.payment_method_id) > 0 ? plan.payment_method_id : undefined;
    renderSavedCards($picker, cards, savedCardId);
    $modal.find('.mgr-pay-method-saved').toggle(cards.length > 0);

    mgrPledgeModalSyncPaymentMethodPanels($modal);
}

function mgrPledgeBillInitExpiryInputMask($modal) {
    if (!$modal?.length) {
        return;
    }
    FieldValidation.applyCardMasks($modal, {
        cvv: '#mgr_pledge_bill_cvv2',
        expiryMonth: '#mgr_pledge_bill_expiry_month',
        expiryYear: '#mgr_pledge_bill_expiry_year',
    });
}

function mgrPledgeBillValidateNewCard($modal) {
    const daf = getMgrPledgeBillDafCardType();
    const isCredit = daf === 'credit';

    FieldValidation.clearValidity($modal.find(
        '#mgr_pledge_bill_name, #mgr_pledge_bill_card, #mgr_pledge_bill_expiry_month, #mgr_pledge_bill_expiry_year, #mgr_pledge_bill_cvv2, #mgr_pledge_bill_avs_zip'
    ));

    const name = String($modal.find('#mgr_pledge_bill_name').val() || '').trim();
    if (!name) {
        FieldValidation.reportFieldError($modal.find('#mgr_pledge_bill_name'), FieldValidation.messages.card.name);
        return { ok: false, errors: [FieldValidation.messages.card.name] };
    }

    const valid = FieldValidation.validateCardPaymentFields($modal, {
        card: '#mgr_pledge_bill_card',
        cvv: '#mgr_pledge_bill_cvv2',
        expiryMonth: '#mgr_pledge_bill_expiry_month',
        expiryYear: '#mgr_pledge_bill_expiry_year',
        postalCode: '#mgr_pledge_bill_avs_zip',
    }, { requireCreditFields: isCredit });

    if (valid !== true) {
        return { ok: false, errors: ['Please check the card fields.'] };
    }

    return { ok: true, errors: [] };
}

registerDafCardTypeHandler('mgr_pledge_bill_', function (type) {
    const cardType = String(type || 'credit');
    setMgrPledgeBillDafCardType(cardType);
    const $el = $('#mgr_pledge_bill_cc_div:visible').first();
    if (!$el.length) return;
    const $modal = pledgePayUiRoot($el);
    $modal.find('.mgr-pledge-bill-zip-row').toggle(cardType === 'credit');
    mgrPledgeModalSyncPaymentMethodPanels($modal);
});

$(document).on('change input', '#setPledgePaymentModal .mgr-gateway-use-credit, #setPledgePaymentModal .mgr-gateway-credit-apply, #pledge_pay_body .mgr-gateway-use-credit, #pledge_pay_body .mgr-gateway-credit-apply', function () {
    const $shell = pledgePayUiRoot($(this));
    mgrPledgeModalSyncQrPayButtonText($shell);
});

$(document).on('change', '#setPledgePaymentModal input[name="mgr_pledge_payment_method_pref"], #pledge_pay_body input[name="mgr_pledge_payment_method_pref"]', function () {
    const $shell = pledgePayUiRoot($(this));
    mgrPledgeModalSyncPaymentMethodPanels($shell);
    mgrPledgeModalSyncQrPayButtonText($shell);
    const kind = selectedPaymentKind($shell);
    const $payNow = $shell.find('#pay_now_button');
    if ($payNow.length && !isPendingRecurringPayBody($shell)) {
        $payNow.text(kind === 'check_cash' ? 'Apply Payment' : 'Make Payment');
    }
    const $label = $shell.find('#send_paid_receipt_label');
    const $checkbox = $shell.find('#send_paid_receipt');
    if ($label.length && $checkbox.length && kind === 'check_cash') {
        const email = String($shell.find('#payment_email').val() || '').trim();
        if (email !== '') {
            $label.text('Send Receipt to ' + email).removeClass('disabled_text');
            $checkbox.prop('disabled', false);
        } else {
            $label.text('Send Receipt — no email on file').addClass('disabled_text');
            $checkbox.prop('checked', false).prop('disabled', true);
        }
    }
    $shell.trigger('pledge-pay:summary-refresh');
});

$(document).on('change', '#setPledgePaymentModal #mgr_pledge_bill_saved_payment_id, #pledge_pay_body #mgr_pledge_bill_saved_payment_id', function () {
    const $shell = pledgePayUiRoot($(this));
    mgrPledgeModalSyncPaymentMethodPanels($shell);
});

document.addEventListener('saved-card:add-new', function (event) {
    const picker = event.detail && event.detail.picker;
    if (!picker || picker.getAttribute('data-saved-card-picker') !== 'mgr_pledge_bill_saved_payment_id') {
        return;
    }
    clearSavedCardSelection(picker);
});

$(document).on('click', '#setPledgePaymentModal [data-mgr-pledge-method-pill], #pledge_pay_body [data-mgr-pledge-method-pill]', function (e) {
    const $pill = $(this);
    if ($pill.hasClass('is-disabled') || $pill.prop('disabled')) {
        e.preventDefault();
        return false;
    }
    const val = String($pill.data('mgrPledgeMethodPill') || '');
    const $shell = pledgePayUiRoot($pill);
    const $radio = $shell.find(`input[name="mgr_pledge_payment_method_pref"][value="${val}"]`);
    if ($radio.length && !$radio.prop('disabled')) {
        $radio.prop('checked', true).trigger('change');
    }
    return false;
});

$(document).on('click mousedown', '#setPledgePaymentModal [data-mgr-pledge-method-pill].is-disabled, #pledge_pay_body [data-mgr-pledge-method-pill].is-disabled', function (e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    return false;
});

$(document).on('click', '#setPledgePaymentModal #mgr_pledge_qr_pay_btn, #pledge_pay_body #mgr_pledge_qr_pay_btn', function () {
    const $shell = pledgePayUiRoot($(this));
    const $btn = $(this);
    const setupUrl = getPledgePayConfig().memberPledgePaySetupUrl;
    if (!setupUrl) {
        throw new Error('memberPledgePaySetupUrl is required');
    }
    if (!validatePledgeBillPaymentState($shell)) {
        return;
    }
    const payload = buildQrPledgePaymentPayload($shell, {
        csrfToken: window.csrfToken || $('meta[name="csrf-token"]').attr('content') || '',
    });

    $btn.prop('disabled', true);
    showOverlay(getManagerOverlayElement(), { immediate: true });
    $.ajax({
        url: setupUrl,
        type: 'POST',
        data: payload,
        success: function (res) {
            const finish = () => {
                hideOverlay(getManagerOverlayElement(), { immediate: true });
                $btn.prop('disabled', false);

                if (res && res.success && res.fully_covered) {
                    mgrPledgeBillShellDismissIfModal($shell);
                    if ($('#mgr_index_paging').length) {
                        runAfterPledgePaymentModalHidden($shell, () => {
                            reloadPledgesPage(() => {
                                showAlertMsg(res.msg || 'Account credit applied.');
                            }, readOpenPayRowIds());
                        });
                        return;
                    }
                    if (res.page_info) {
                        $('#index_paging').empty().html(res.page_info).fadeIn('slow');
                        return;
                    }
                    showAlertMsg(res.msg || 'Account credit applied.');
                    if (getPledgePayConfig().pledgePaymentShell === 'page') {
                        window.location.reload();
                    }
                    return;
                }
                if (res && res.success) {
                    mgrPledgeBillShellDismissIfModal($shell);
                    openQrPayModal();
                    return;
                }
                showAlertMsg(res && (res.error || res.msg) ? (res.error || res.msg) : 'Could not start QR pay. Please try again.');
            };

            if (res && res.success && res.fully_covered) {
                mgrPledgeBillRunAfterFullyCoveredDelay(finish);
                return;
            }
            finish();
        },
        error: function () {
            hideOverlay(getManagerOverlayElement(), { immediate: true });
            $btn.prop('disabled', false);
            showAlertMsg('Could not start QR pay. Please try again.');
        }
    });
});

$(document).on('input', '#setPledgePaymentModal #mgr_pledge_bill_card, #pledge_pay_body #mgr_pledge_bill_card', function () {
    const $inp = $(this);
    let v = $inp.val();
    const last = v.slice(-1);
    if (last && isNaN(last)) {
        $inp.val(v.slice(0, -1));
        return;
    }
    const amexTest = v.replace(/\s/g, '');
    if (amexTest.match(/^3[47]\d{2}/)) {
        $inp.val(amexTest.replace(/(\d{4})\s*(\d{6})?\s*(\d{5})?/, '$1 $2 $3').replace(/\s\s/g, ' ').trim().substring(0, 17));
    } else {
        $inp.val(amexTest.replace(/(\d{4})(?=\d)/g, '$1 '));
    }
});


export {
    applyPledgePaySavedCardsToUi,
    getMgrPledgeBillDafCardType,
    mgrPledgeBillInitExpiryInputMask,
    mgrPledgeBillValidateNewCard,
    mgrPledgeModalSyncPaymentMethodPanels,
    mgrPledgeModalSyncPaymentMethodPills,
    mgrPledgeModalSyncQrPayButtonText,
    resetMgrPledgeBillDafCardType,
    setMgrPledgeBillDafCardType,
};
